/**
 * Debounce function that delays execution until after delay milliseconds have elapsed
 * since the last time the debounced function was invoked.
 *
 * @param func - The function to debounce
 * @param delay - The number of milliseconds to delay
 * @param options - Configuration options
 * @returns A debounced function with cancel and flush methods
 */
export function debounce<T extends (...args: unknown[]) => any>(
  func: T,
  delay: number,
  options: {
    leading?: boolean;
    trailing?: boolean;
  } = {}
): T & { cancel: () => void; flush: () => ReturnType<T> | undefined } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastThis: unknown = null;
  let result: ReturnType<T>;

  const { leading = false, trailing = true } = options;

  const invokeFunc = (): ReturnType<T> => {
    if (lastArgs !== null) {
      result = func.apply(lastThis, lastArgs);
      lastArgs = null;
      lastThis = null;
    }
    return result;
  };

  const leadingEdge = (): ReturnType<T> => {
    timeoutId = setTimeout(timerExpired, delay);
    return leading ? invokeFunc() : result;
  };

  const timerExpired = (): void => {
    timeoutId = null;
    if (trailing && lastArgs !== null) {
      invokeFunc();
    }
  };

  const debouncedFunc = function (this: any, ...args: Parameters<T>): ReturnType<T> {
    const isInvoking = timeoutId === null;

    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this;

    if (isInvoking) {
      return leadingEdge();
    }

    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(timerExpired, delay);
    return result;
  } as T & { cancel: () => void; flush: () => ReturnType<T> | undefined };

  debouncedFunc.cancel = (): void => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastArgs = null;
    lastThis = null;
  };

  debouncedFunc.flush = (): ReturnType<T> | undefined => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
      return invokeFunc();
    }
    return result;
  };

  return debouncedFunc;
}
